import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-modal',
  imports: [TranslocoModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.css'
})
export class ModalComponent {
  /** Modal title */
  @Input() title: string = '';
  /** Modal message */
  @Input() message: string = '';
  /** Determines if this is a confirmation modal */
  @Input() isConfirm: boolean = false;

  /** Message shown on confirm button (if visible) */
  @Input() confirmText: string = '';
  /** Message shown on cancel button (if visible) */
  @Input() cancelText: string = '';
  /** Message shown on close button (if visible) */
  @Input() closeText: string = '';
  /**
   * Event emitted when the modal is closed.
   * Emits true for confirmation/close and false for cancellation.
   */
  @Output() closed: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private translocoService: TranslocoService) {}

  /**
   * Gets the confirm button text, using custom text if provided, otherwise falls back to translation
   */
  get confirmButtonText(): string {
    return this.confirmText || this.translocoService.translate('modal.confirm');
  }

  /**
   * Gets the cancel button text, using custom text if provided, otherwise falls back to translation
   */
  get cancelButtonText(): string {
    return this.cancelText || this.translocoService.translate('modal.cancel');
  }

  /**
   * Gets the close button text, using custom text if provided, otherwise falls back to translation
   */
  get closeButtonText(): string {
    return this.closeText || this.translocoService.translate('modal.close');
  }

  /**
   * Called when the user confirms the action.
   */
  onConfirm(): void {
    this.closed.emit(true);
  }

  /**
   * Called when the user cancels the action.
   */
  onCancel(): void {
    this.closed.emit(false);
  }

  /**
   * Called when the user simply closes the modal (for info modals).
   */
  onClose(): void {
    this.closed.emit(true);
  }

  /**
   * Called when the backdrop is clicked.
   * For confirmation modals, treat this as cancellation.
   */
  onBackdropClick(): void {
    if (this.isConfirm) {
      this.onCancel();
    } else {
      this.onClose();
    }
  }
}
